// Chess lobby body. Mirrors Ludo's SetupScreen layout 1:1 — hero
// (logo + online pill) takes all flex-grow space, then a bottom stack
// of pawn tile + cash grid pushed down with `mt-auto`. The top bar
// lives in Page.tsx so it's shared across every phase, matching
// Ludo's structure (this component deliberately renders NO top-bar
// chrome).
//
// Only the colours differ from Ludo — wood-tone brown/cream palette
// on the tiles instead of Ludo's purple/gold, and the gold border
// swapped for a warmer `#8b6636` wood frame. Every sizing /
// padding / gap value is copied byte-for-byte from
// `frontend/src/games/ludo/Page.tsx:1640-1700` so the two lobbies
// feel the same across games.
//
// Per-game scoping: every classable rule uses `chess-*` names so
// future Ludo / X&O / Rummy styling tweaks can't collide with chess.

import Icon from '@baaligames/shared-ui/components/Icon';
import { playTap } from './sounds';

const CASH_IMAGES = [
  '/games/chess/Cash-1.png',
  '/games/chess/Cash-2.png',
  '/games/chess/Cash-3.png',
  '/games/chess/Cash-4.png',
];

type Props = {
  authReady: boolean;
  onlinePlayers: number;
  betTiers: number[];
  selectedBet: number | null;
  platformFeePercent: number;
  waitingByBet: Record<string, number>;
  showWaitingBadge: boolean;
  /** Time control surfaced as the "BLITZ 5|3" chip. Straight out of
   *  ChessGame config so admin edits flow to the lobby live. */
  baseMinutes:      number;
  incrementSeconds: number;
  onSelectBet: (n: number) => void;
};

function fmt(n: number) {
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

export default function LobbyScreen(p: Props) {
  // Include zero-amount tiers too — they render as "FREE" so admins
  // can offer free-play stakes by setting a BET_TIERS entry to 0.
  const cashes = p.betTiers.slice(0, 4);
  return (
    <div
      id="chess-setup-screen"
      className="flex flex-col w-full h-full relative transition-all duration-300 flex-grow"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {!p.authReady && (
        <div className="absolute inset-0 grid place-items-center z-50 bg-black/60">
          <div className="text-[#f6d07c] font-bold tracking-widest">CONNECTING…</div>
        </div>
      )}

      {p.authReady && (
        <div className="flex flex-col flex-grow w-full">
          {/* ─── Hero — logo + online pill, centered, flex-grow ─── */}
          <div className="flex-grow flex flex-col justify-center items-center px-4 sm:px-6 min-h-[150px]">
            <img
              src="/games/chess/Logo.png"
              alt=""
              className="w-full h-full object-contain drop-shadow-2xl"
              style={{ maxHeight: 250 }}
            />
            <div className="mt-4 text-[#f6d07c] text-[10px] sm:text-xs font-bold tracking-widest bg-black/50 px-4 py-2 rounded-full border border-[#8b6636] flex items-center justify-center gap-2">
              <span
                className="inline-block w-2 h-2 rounded-full bg-[#22c55e] shadow-[0_0_6px_rgba(34,197,94,0.8)]"
                aria-hidden
              />
              <span>{p.onlinePlayers} PLAYERS ONLINE</span>
            </div>
          </div>

          {/* ─── Bottom stack: 2-PLAYER tile + 2×2 cash grid ─────── */}
          <div className="px-4 sm:px-5 mt-auto flex flex-col gap-3 w-full pb-6">
            {/* No player-count selector — classical chess is strictly
                2-player so there's nothing to pick. Instead we surface
                the time control as an informational chip (mirrors
                X&O's "Match Format · Best of 3" affordance). The
                values come straight from ChessGame config so admin
                edits to BASE_MINUTES_PER_SIDE / INCREMENT_SECONDS_PER_MOVE
                take effect live. */}
            <div className="flex items-center justify-between gap-3 mb-2 sm:mb-4 px-1">
              <span className="text-[10px] sm:text-xs font-bold tracking-widest text-[#f6d07c]/80 uppercase">
                Match Format
              </span>
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-b from-[#5a3f26] to-[#2b1a0e] border border-[#8b6636] text-[#f6d07c] text-[11px] sm:text-xs font-black tracking-widest shadow-[0_3px_8px_rgba(0,0,0,0.5)]"
              >
                <span aria-hidden>♟</span>
                <span>
                  BLITZ {p.baseMinutes}<span className="opacity-60 mx-0.5">|</span>{p.incrementSeconds}
                </span>
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {cashes.map((val, i) => {
                // Classical chess is strictly 2-player → always *2.
                const winAmt = Math.floor(val * 2 * (100 - p.platformFeePercent) / 100);
                const waiting = Number(p.waitingByBet?.[String(val)] || 0);
                const showBadge = p.showWaitingBadge && waiting > 0;
                const isSelected = p.selectedBet === val;
                return (
                  <button
                    key={val}
                    data-val={val}
                    onClick={() => { playTap(); p.onSelectBet(val); }}
                    className={[
                      'chess-cash-btn relative w-full py-3 sm:h-[95px] rounded-xl border-[3px] border-[#8b6636]',
                      'bg-gradient-to-b from-[#5a3f26] to-[#2b1a0e] flex flex-row items-center justify-between',
                      'pl-3 sm:pl-4 pr-1 sm:pr-2 shadow-[0_6px_15px_rgba(0,0,0,0.6)]',
                      isSelected ? 'selected' : '',
                    ].join(' ')}
                  >
                    {showBadge && (
                      <span
                        className="chess-waiting-badge"
                        aria-label={`${waiting} waiting`}
                        title={`${waiting} waiting at this stake`}
                      >
                        <Icon name="group" size={10} />
                        <span>{waiting}</span>
                      </span>
                    )}
                    <div className="flex flex-col items-start justify-center pt-1 pointer-events-none">
                      <span
                        className={
                          'text-white font-black leading-none tracking-tighter '
                          + (val === 0 ? 'text-[22px] sm:text-[30px]' : 'text-[32px] sm:text-[42px]')
                        }
                        style={{ textShadow: '0 4px 6px rgba(0,0,0,0.6)' }}
                      >
                        {val === 0 ? 'FREE' : fmt(val)}
                      </span>
                      <span
                        className="text-[#f6d07c] font-bold text-[10px] sm:text-sm tracking-widest mt-1"
                        style={{ textShadow: '0 2px 3px rgba(0,0,0,0.6)' }}
                      >
                        {val === 0 ? 'PLAY FOR FUN' : `WIN ${fmt(winAmt)}`}
                      </span>
                    </div>
                    <img
                      src={CASH_IMAGES[i] || CASH_IMAGES[CASH_IMAGES.length - 1]}
                      alt=""
                      className="w-[50px] h-[50px] sm:w-[75px] sm:h-[75px] object-contain drop-shadow-lg pointer-events-none"
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
